<template>
  <div v-show="showDeviceInfo">
    <q-card v-if="device?.uid" square flat bordered class="my-card bg-grey-1">
      <q-card-section>
        <div class="row items-center no-wrap">
          <div class="col">
            <div class="text-grey-brand text-h6 text-bold">
              <q-avatar
                size="xs"
                :color="device.isConnected ? 'positive' : 'grey-6'"
                text-color="white"
              />
              UID: {{ device ? device.uid : '' }}

              <span class="float-right">
                <q-btn
                  flat
                  round
                  dense
                  icon="highlight_off"
                  color="red-4"
                  @click="clearSelection"
                >
                  <q-tooltip>Clear selection</q-tooltip>
                </q-btn>
                <span
                  v-if="device.isIridium"
                  v-show="device.lastSignalStrength"
                >
                  <img
                    v-if="(device.lastSignalStrength = 5)"
                    class="q-mr-sm"
                    src="signal-5.png"
                  />
                  <img
                    v-else-if="(device.lastSignalStrength = 4)"
                    class="q-mr-sm"
                    src="signal-4.png"
                  />
                  <img
                    v-else-if="(device.lastSignalStrength = 3)"
                    class="q-mr-sm"
                    src="signal-3.png"
                  />
                  <img
                    v-else-if="(device.lastSignalStrength = 2)"
                    class="q-mr-sm"
                    src="signal-2.png"
                  />
                  <img v-else class="q-mr-sm" src="signal-1.png" />
                </span>
                <span v-else v-show="device.lastSignalStrength">
                  <img
                    v-if="device.lastSignalStrength >= -80"
                    class="q-mr-sm"
                    src="signal-5.png"
                    :title="device.lastSignalStrength + ' dBm'"
                  />
                  <img
                    v-else-if="device.lastSignalStrength >= -90"
                    class="q-mr-sm"
                    src="signal-4.png"
                    :title="device.lastSignalStrength + ' dBm'"
                  />
                  <img
                    v-else-if="device.lastSignalStrength > -100"
                    class="q-mr-sm"
                    src="signal-3.png"
                    :title="device.lastSignalStrength + ' dBm'"
                  />
                  <img
                    v-else-if="device.lastSignalStrength < -100"
                    class="q-mr-sm"
                    src="signal-2.png"
                    :title="device.lastSignalStrength + ' dBm'"
                  />
                  <img
                    v-else
                    class="q-mr-sm"
                    src="signal-1.png"
                    :title="device.lastSignalStrength + ' dBm'"
                  />
                </span>
                <span v-show="device.lastBattery">
                  <q-icon
                    v-if="device.lastBattery > 9000"
                    name="fas fa-battery-full"
                    title="100%"
                  />
                  <q-icon
                    v-else-if="device.lastBattery > 8500"
                    name="fas fa-battery-three-quarters"
                    title="80%"
                  />
                  <q-icon
                    v-else-if="device.lastBattery > 8000"
                    name="fas fa-battery-three-quarters"
                    title="60%"
                  />
                  <q-icon
                    v-else-if="device.lastBattery > 7500"
                    name="fas fa-battery-half"
                    title="40%"
                  />
                  <q-icon
                    v-else-if="device.lastBattery > 7000"
                    name="fas fa-battery-quarter"
                    title="20%"
                  />
                  <q-icon v-else name="fas fa-battery-empty" title="0%" />
                </span>
                <span v-show="device.isIridium">
                  <q-icon
                    class="q-ml-sm"
                    name="fas fa-satellite-dish"
                    title="Iridium"
                  />
                </span>
              </span>
            </div>
          </div>
        </div>
      </q-card-section>

      <q-card-section v-if="linkedPigRun.length">
        <p class="text-weight-bold">Active Pig Run: {{ linkedPigRun }}</p>
        <q-btn
          size="sm"
          icon="fas fa-map-marker"
          class="btn-brand"
          label="Go To Pig Run"
          no-caps
          @click="goToPigRun"
        />
      </q-card-section>

      <q-card-section>
        <p v-if="getuser() == 'superadmin'">
          <span style="color: red">Currently logged on as super admin.</span>
        </p>

        <p>
          Heartbeat:
          <span class="timestamp">{{
            device && device.lastHeartbeat
              ? device.lastHeartbeat.slice(0, -7)
              : ''
          }}</span>
        </p>

        <!-- Cellular carrier display. -->
        <p v-if="displayCellularCarrierInfo">
          <span class="timestamp"
            >Primary Carrier: {{ cellularCarrierInfoPrimaryCarrier }}</span
          ><br />
          <span class="timestamp"
            >Active Carrier: {{ cellularCarrierInfoActiveCarrier }}</span
          ><br />
          <span class="timestamp"
            >Status: {{ cellularCarrierInfoRegistrationStatus }}</span
          >
        </p>

        <!-- Disk usage display by size. -->
        <div v-if="displayDiskUsageBySize">
          <div v-if="displayDiskUsageBySizeAsWarning">
            <span style="color: red"
              >Disk Usage (MB): {{ diskUseMBytes }} of
              {{ diskSizeMBytes }}</span
            ><br />
            <span style="color: red">Disk format recommended.</span><br />
            <br />
          </div>
          <div v-else>
            <span class="timestamp"
              >Disk Usage (MB): {{ diskUseMBytes }} of
              {{ diskSizeMBytes }}</span
            ><br /><br />
          </div>
        </div>

        <!-- Disk usage display by file count. -->
        <div v-if="displayDiskUsageByFileCount">
          <div v-if="displayDiskUsageByFileCountAsWarning">
            <span style="color: red"
              >Disk Usage (File Counts): Offline =
              {{
                numberOfflineFiles != undefined
                  ? numberOfflineFiles
                  : 'Unknown'
              }}, Online =
              {{
                numberOnlineFiles != undefined ? numberOnlineFiles : 'Unknown'
              }}</span
            ><br />
            <span style="color: red">Disk format recommended.</span><br /><br />
          </div>
          <div v-else>
            <span style="timestamp"
              >Disk Usage (File Counts): Offline =
              {{
                numberOfflineFiles != undefined
                  ? numberOfflineFiles
                  : 'Unknown'
              }}, Online =
              {{
                numberOnlineFiles != undefined ? numberOnlineFiles : 'Unknown'
              }}</span
            ><br /><br />
          </div>
        </div>

        <p>Power Mode: {{ device ? device.lastPowerMode : '' }}</p>
        <p v-if="device.lastPowerMode !== 'Active'">
          Schedule: {{ device.schedule }}
        </p>
        <p v-if="lastPassage">
          Last Passage:
          <span class="timestamp">{{ lastPassage.slice(0, -7) }}</span>
          <q-btn
            v-for="passage in lastPassagesData"
            :key="passage.passageId"
            size="xs"
            class="q-mr-xs btn-brand"
            dense
            icon="show_chart"
            :label="getPassageWaveformLabel(passage)"
            @click="showChart(passage)"
          />
        </p>
        <p v-else>Last Passage: None</p>
        <p v-if="!device.isIridium">
          Stream Mode: {{ lastStreamModeText() }}
          <q-btn
            size="xs"
            class="btn-brand"
            dense
            icon="fas fa-headphones"
            @click="showStreamer(device.uid)"
          >
          </q-btn>
        </p>
        <span v-else
          ><!-- Iridium -->
          <p>Passage State: {{ passageStateText() }}</p>
          <p>Passage Size: {{ passageSizeText() }}</p>
        </span>
        <p>
          Record Mode:
          {{ device.lastRecordMode ? device.lastRecordMode : 'Unknown' }}
        </p>
        <p v-if="showGeoMode">
          Geo Mode: {{ formatGeoSetting(device.lastGeoMode) }}
        </p>
        <p v-if="device.lastFrequency0Setting >= 0">
          Detectors:
          <span class="float-right q-pr-xl"
            >{{ 'Mag' }}({{ lastSensitivityText(device.lastSensitivity0) }})
            <q-icon :name="device.lastEnable0 ? 'check' : 'close'" /></span
          ><br />
          <span class="float-right q-pr-xl"
            >{{ formatDetectorSetting(device.lastFrequency1Setting) }}({{
              lastSensitivityText(device.lastSensitivity1)
            }}) <q-icon :name="device.lastEnable1 ? 'check' : 'close'" /></span
          ><br />
          <span class="float-right q-pr-xl"
            >{{ formatDetectorSetting(device.lastFrequency2Setting) }}({{
              lastSensitivityText(device.lastSensitivity2)
            }}) <q-icon :name="device.lastEnable2 ? 'check' : 'close'" /></span
          ><br />
          <span class="float-right q-pr-xl"
            >{{ formatDetectorSetting(device.lastFrequency3Setting) }}({{
              lastSensitivityText(device.lastSensitivity3)
            }}) <q-icon :name="device.lastEnable3 ? 'check' : 'close'" /></span
          ><br />
          <span
            v-if="device.deviceType === 'AGMVECTOR'"
            class="float-right q-pr-xl"
            >{{ 'In-Geo' }}({{ lastSensitivityText(device.lastSensitivity4) }})
            <q-icon :name="device.lastEnable4 ? 'check' : 'close'" /></span
          ><br v-if="device.deviceType === 'AGMVECTOR'" />
          <span
            v-if="device.deviceType === 'AGMVECTOR'"
            class="float-right q-pr-xl"
            >{{ 'Tmr' }}({{ lastSensitivityText(device.lastSensitivity5) }})
            <q-icon :name="device.lastEnable5 ? 'check' : 'close'"
          /></span>
        </p>

        <p v-if="expectedWakeupTime.length">
          Expected Wakeup Time:
          <span class="timestamp">{{ expectedWakeupTime }}</span>
        </p>
        <p v-if="shutdownTime.length && !device.isConnected">
          Shutdown Time: <span class="timestamp">{{ shutdownTime }}</span>
        </p>
        <br />
      </q-card-section>

      <q-separator />

      <DeviceActions v-if="getuser() == 'superadmin' || getuser() == 'admin'" />
      <ViewerDeviceActions v-if="getuser() == 'viewer'" />
    </q-card>
    <q-card v-else square flat bordered class="my-card bg-grey-1">
      <q-card-section>
        <div class="row items-center no-wrap">
          <div class="col">
            <div class="text-h6 text-weight-bold text-grey-brand">
              No Devices Selected
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DeviceActions from '@/components/DeviceActions.vue';
import ViewerDeviceActions from '@/components/ViewerDeviceActions.vue';
import { StreamMode } from '@/models/StreamMode';
import { PassageState } from '@/models/PassageState';
import { PassageSize } from '@/models/PassageSize';
import { WaveformTypeLabel } from '@/models/PassageWaveform';
import { IPassage } from '@/models/Passage';

export default defineComponent({
  name: 'DeviceInfo',
  components: {
    DeviceActions,
    ViewerDeviceActions,
  },
  data() {
    return {
      showGeoMode: false,
      cellularCarrierEntry: null as any,
      diskUseEntry: null as any,
      unsubscribe: null as (() => void) | null,
      refreshDebounceTimer: null as ReturnType<typeof setTimeout> | null,
    };
  },
  computed: {
    showDeviceInfo(): boolean {
      return this.$store.state.showDeviceInfo;
    },
    device(): any {
      return this.$store.state.device.selectedDevice;
    },
    activePigRuns(): any[] {
      return this.$store.getters.activePigRunsList || [];
    },
    linkedPigRun(): string {
      const uid = this.device?.uid;
      if (!uid) return '';
      for (const pigRun of this.activePigRuns) {
        if (pigRun?.devices?.some((d: any) => d.uid === uid)) {
          return pigRun.name;
        }
      }
      return '';
    },
    lastPassage(): string | null {
      if (this.device?.lastPassage) {
        return this.device.lastPassage;
      }
      return null;
    },
    lastPassagesData(): IPassage[] {
      // TODO: SET_LAST_PASSAGES is currently commented out in the store action updateLastPassage
      // Once that is fixed, use: return this.$store.state.lastPassages || [];
      return this.$store.state.passage.selectedPassages || [];
    },
    expectedWakeupTime(): string {
      if (this.device?.lastExpectedWakeupTime) {
        const wakeup = new Date(this.device.lastExpectedWakeupTime + 'Z');
        if (wakeup > new Date()) {
          return this.device.lastExpectedWakeupTime.toString().slice(0, -7);
        }
      }
      return '';
    },
    shutdownTime(): string {
      if (this.device?.lastShutdownTime) {
        return this.device.lastShutdownTime.toString().slice(0, -7);
      }
      return '';
    },
    map(): any {
      return this.$store.state.map;
    },
    displayCellularCarrierInfo(): boolean {
      return this.cellularCarrierEntry != null;
    },
    cellularCarrierInfoPrimaryCarrier(): string {
      return this.cellularCarrierEntry?.primaryCarrier || '';
    },
    cellularCarrierInfoActiveCarrier(): string {
      return this.cellularCarrierEntry?.activeCarrier || '';
    },
    cellularCarrierInfoRegistrationStatus(): string {
      return this.cellularCarrierEntry?.registrationStatus || '';
    },
    displayDiskUsageBySize(): boolean {
      return this.diskUseEntry != null;
    },
    displayDiskUsageBySizeAsWarning(): boolean {
      const el = this.diskUseEntry;
      if (!el) return false;
      return el.diskUseMBytes / el.diskSizeMBytes > 0.5;
    },
    diskUseMBytes(): number {
      return this.diskUseEntry?.diskUseMBytes || 0;
    },
    diskSizeMBytes(): number {
      return this.diskUseEntry?.diskSizeMBytes || 0;
    },
    displayDiskUsageByFileCount(): boolean {
      return this.displayDiskUsageBySize;
    },
    displayDiskUsageByFileCountAsWarning(): boolean {
      const el = this.diskUseEntry;
      if (!el) return false;
      return (
        (el.numberOfflineFiles != null && el.numberOfflineFiles > 500) ||
        (el.numberOnlineFiles != null && el.numberOnlineFiles > 500)
      );
    },
    numberOfflineFiles(): number | undefined {
      return this.diskUseEntry?.numberOfflineFiles;
    },
    numberOnlineFiles(): number | undefined {
      return this.diskUseEntry?.numberOnlineFiles;
    },
  },
  watch: {
    device: {
      handler() {
        this.checkFirmware();
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    this.unsubscribe = this.$store.subscribeAction((action: any) => {
      if (action.type === 'setCellularCarrierInfo') {
        const data = action.payload;
        if (data?.uid === this.device?.uid) {
          this.cellularCarrierEntry = data;
        }
      }
      if (action.type === 'setDiskUse') {
        const data = action.payload;
        if (data?.uid === this.device?.uid) {
          this.diskUseEntry = data;
        }
      }
      if (action.type === 'passageRecieved') {
        this.onSignalRDeviceUpdate(action.payload);
      }
      if (action.type === 'heartbeatBatchReceived') {
        const batch: any[] = action.payload;
        if (batch.some((d: any) => d.uid === this.device?.uid)) {
          this.onSignalRDeviceUpdate({ uid: this.device!.uid });
        }
      }
    });
  },
  beforeUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
    }
  },
  methods: {
    getuser(): string {
      return this.$store.state.user?.role || '';
    },
    clearSelection() {
      this.$store.dispatch('clearSelection');
    },
    onSignalRDeviceUpdate(data: any) {
      const uid = data?.uid ?? data;
      if (!uid || uid !== this.device?.uid) return;
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
      }
      this.refreshDebounceTimer = setTimeout(() => {
        this.$store.dispatch('getDevice', uid);
      }, 1500);
    },
    checkFirmware() {
      this.showGeoMode = !!(
        this.device?.firmwareVersion && this.device.firmwareVersion > 2.3
      );
    },
    goToPigRun() {
      const uid = this.device?.uid;
      if (!uid) return;
      for (const pigRun of this.activePigRuns) {
        if (pigRun?.devices?.some((d: any) => d.uid === uid)) {
          this.$store.commit('SET_SELECTED_PIG_RUN_ID', pigRun.pigRunId);
          const latLngs = (pigRun.markers || [])
            .filter((m: any) => m.latitude && m.longitude)
            .map((m: any) => [m.latitude, m.longitude]);
          if (latLngs.length && this.map) {
            this.map.fitBounds(latLngs, { maxZoom: 15, padding: [50, 50] });
          }
          return;
        }
      }
    },
    showChart(passage: any) {
      const passageIdsToUpdate = [passage.passageId];
      this.$store.dispatch('addPassageIdsToUpdate', passageIdsToUpdate);
      this.$store.dispatch('setPassageDialogSource', 'selected_passage');
      this.$store.dispatch('passageChartShow', passage);
    },
    getPassageWaveformLabel(passage: any): string {
      const wf = passage.passageWaveforms?.[0];
      if (wf)
        return (
          WaveformTypeLabel[wf.passageWaveformType] || wf.passageWaveformType
        );
      return 'DATA';
    },
    showStreamer(uid: number) {
      this.$store.dispatch('realtimeStreamerShow', uid);
    },
    lastStreamModeText(): string {
      const mode = this.device?.lastStreamMode;
      if (StreamMode.getModes().includes(mode)) {
        return StreamMode.toString(mode);
      }
      return 'BT/Cell';
    },
    passageStateText(): string {
      if (this.device?.psgState !== undefined) {
        return PassageState.byIdx(this.device.psgState);
      }
      return 'Unknown';
    },
    passageSizeText(): string {
      if (this.device?.psgSize !== undefined) {
        return PassageSize.byIdx(this.device.psgSize);
      }
      return 'Unknown';
    },
    formatDetectorSetting(setting: number): string {
      if (setting === 0) return 'Mag';
      return String(setting);
    },
    lastSensitivityText(sensitivity: number): string {
      const map: Record<number, string> = {
        0: 'UL',
        1: 'VL',
        2: 'L',
        3: 'M',
        4: 'H',
        5: 'VH',
        6: 'UH',
      };
      return map[sensitivity] || '';
    },
    formatGeoSetting(setting: number): string {
      const map: Record<number, string> = {
        0: 'Low',
        1: 'Medium',
        2: 'High',
        3: 'Very High',
      };
      return map[setting] || 'Unknown';
    },
  },
});
</script>

<style scoped>
.timestamp {
  white-space: nowrap;
}
</style>
